// What Query Details shows for a published query: its CPM and the category it
// was published under. The queries list does not carry either, so they come from
// the federation's published list (FQS GET /query/published/federation), which
// is keyed here by query id.

import { validateCpm } from "../NewQuery/cpm";

export interface PublishedDetails {
  cpm: number | null;
  categoryId: string;
  categoryName: string;
  categoryDescription: string;
}

export const UNCATEGORISED = "Uncategorised";

const rupees = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

// A CPM that is missing or not above zero reads as "not set" (publishing
// without one stores zero), so it shows as a dash and never as ₹0.
export const formatCpm = (cpm: number | null | undefined): string =>
  typeof cpm === "number" && Number.isFinite(cpm) && cpm > 0
    ? `₹${rupees.format(cpm)}`
    : "—";

// A query published without a category has empty category fields.
export const categoryLabel = (details?: PublishedDetails): string =>
  details?.categoryName?.trim() || UNCATEGORISED;

export const indexPublishedByQuery = (
  data: any,
): Record<string, PublishedDetails> => {
  const byQuery: Record<string, PublishedDetails> = {};
  (data?.items ?? data?.Items ?? []).forEach((item: any) => {
    if (!item?.query_id) return;
    const cpm = Number(item.cpm);
    byQuery[item.query_id] = {
      cpm: Number.isFinite(cpm) ? cpm : null,
      categoryId: item.category_id ?? "",
      categoryName: item.category_name ?? "",
      categoryDescription: item.category_description ?? "",
    };
  });
  return byQuery;
};

// A published query's CPM is stored by FQS as a whole number (it refuses a
// fraction with a 400), so the edit field takes digits only and the value is
// checked here before it is sent. Above zero, like the New Query form.
export const isWholeCpmInput = (raw: string): boolean => /^\d*$/.test(raw);

export const validateEditedCpm = (
  raw: string,
): { value: number | null; error: string } => {
  const result = validateCpm(raw);
  if (result.value === null) return result;
  if (!Number.isInteger(result.value)) {
    return { value: null, error: "CPM must be a whole number." };
  }
  return result;
};
